import React from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';

import type { ValidationError } from '../../utils/types';

interface ValidationErrorListProps {
  errors: ValidationError[];
  onNavigateToSection: (section: string) => void;
}

/**
 * Renders a list of validation errors with field references
 * and navigation links back to the relevant mapping step sections.
 */
const ValidationErrorList: React.FC<ValidationErrorListProps> = ({
  errors,
  onNavigateToSection,
}) => {
  if (errors.length === 0) return null;

  return (
    <Box data-testid="validation-error-list" aria-live="polite">
      <Alert severity="error" sx={{ mb: 2 }}>
        <Typography variant="body2">
          {errors.length} validation {errors.length === 1 ? 'error' : 'errors'}{' '}
          found. Please fix the issues below before proceeding.
        </Typography>
      </Alert>

      <List dense>
        {errors.map((error, index) => (
          <ListItem
            key={`${error.field}-${index}`}
            data-testid={`validation-error-${index}`}
            secondaryAction={
              error.section ? (
                <Button
                  size="small"
                  onClick={() => onNavigateToSection(error.section!)}
                  aria-label={`Fix ${error.field} error`}
                >
                  Fix
                </Button>
              ) : null
            }
          >
            <ListItemText
              primary={error.field}
              secondary={error.message}
              primaryTypographyProps={{ fontWeight: 'medium' }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default ValidationErrorList;
